// Imports => React
import { Suspense, lazy, useState, useEffect, useMemo, memo } from 'react';
import { withStore } from '@stores';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import clsx from 'clsx';

import {
  THEMES,
} from '@opp/ui-kit';

// Imports => Utilities
import {
  AcIsSet,
} from '@opp/ui-kit';
import { AcFormatCurrencyAmount } from '@utils';

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcHeading,
  AcCard,
  AcLoaderInline
} from '@opp/ui-kit';
import {withTranslation} from "react-i18next";
const AcIncomingOutgoingChart = lazy(() =>
  import(
    '@components/ac-incoming-outgoing-chart/ac-incoming-outgoing-chart'
  )
);

const _CLASSES = {
  MAIN: 'ac-dashboard-stats-tab ac-dashboard__grid-container',
};

const AcDashboardStatsTab = ({ data, store: { global, profile }, i18n, t }) => {
  const { current_chart_data, current_totals, is_loading } = global;
  const { current_partner_currency } = profile;

  const [selectedRange, setSelectedRange] = useState('7');

  useEffect(() => {
    const today = moment();
    const date_start = today.clone().subtract(7, 'days').format('DD-MM-YYYY');
    const date_end = today.clone().format('DD-MM-YYYY');
    global.get_chart_data(t,{
      date_start,
      date_end,
    });
    global.get_totals(t);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  });

  const handleRangeChange = (key, range) => {
    setSelectedRange(key);
    global.get_chart_data(t, range);
  };

  const renderIncomingOutgoingChart = useMemo(() => {
    if (is_loading) return <AcLoaderInline loading />;

    return (
      <Suspense fallback={<AcLoaderInline loading />}>
        <AcIncomingOutgoingChart
          currency={current_partner_currency}
          data={current_chart_data}
          key={'ac-dashboard-stats-incoming-outgoing-chart'}
          range={selectedRange}
          onRangeChange={handleRangeChange}
        />
      </Suspense>
    );
  }, [is_loading, current_chart_data, handleRangeChange, selectedRange, current_partner_currency]);

  const getTotalTransactions = useMemo(() => {
    let result = 0;

    if (AcIsSet(current_totals) && AcIsSet(current_totals.total)) {
      result = current_totals.total;
    }

    // todo language
    let int = new Intl.NumberFormat(i18n.resolvedLanguage, {
      style: 'decimal',
      minimumFractionDigits: 0,
    });

    int = int.format(result);

    return (
      <span>
        {int}
      </span>
    );
  }, [current_totals, is_loading, i18n.resolvedLanguage]);

  const getTotalMoneyProcessed = useMemo(() => {
    let result = 0;

    if (AcIsSet(current_totals) && AcIsSet(current_totals.amount)) {
      result = current_totals.amount;
    }

    return (
      <span>
        {AcFormatCurrencyAmount(result, current_partner_currency, i18n.resolvedLanguage, true)}
      </span>
    );
  }, [current_totals, is_loading, i18n.resolvedLanguage, current_partner_currency]);

  return (
    <div className={getMainClassNames}>
      <AcCard className={'ac-dashboard__column ac-dashboard__column--big'}>
        {renderIncomingOutgoingChart}
      </AcCard>

      <AcCard className={'ac-dashboard__column'}>
        <AcContainer>
          <AcRow>
            <AcColumn>
              <AcHeading theme={THEMES.MEDIUM} rank={6}>
                {t('total-transactions', 'Total transactions', {ns: 'dashboard'})}
              </AcHeading>
            </AcColumn>
          </AcRow>

          <AcRow>
            <AcColumn>
              <AcHeading
                rank={4}
                theme={THEMES.DARK}
                className={'h-padding-y-0 h-margin-y-0'}
              >
                {getTotalTransactions}
              </AcHeading>
            </AcColumn>
          </AcRow>
        </AcContainer>
      </AcCard>

      <AcCard className={'ac-dashboard__column'}>
        <AcContainer>
          <AcRow>
            <AcColumn>
              <AcHeading theme={THEMES.MEDIUM} rank={6}>
                {t('total-money-processed', 'Total money processed', {ns: 'dashboard'})}
              </AcHeading>
            </AcColumn>
          </AcRow>

          <AcRow>
            <AcColumn>
              <AcHeading
                rank={4}
                theme={THEMES.DARK}
                className={'h-padding-y-0 h-margin-y-0'}
              >
                {getTotalMoneyProcessed}
              </AcHeading>
            </AcColumn>
          </AcRow>
        </AcContainer>
      </AcCard>
    </div>
  );
};

export default withTranslation(['dashboard', 'store'])(withStore(observer(AcDashboardStatsTab)));
